




















import { Vue, Component, Watch } from "vue-property-decorator";
import SalesRegionMap from "@/models/map/region";
import Mapbox from "mapbox-gl-vue";
import mapboxgl from "mapbox-gl";
import frankfordMap from "../../map_data/states.json"; // helpful external resource to find lat.long coordinates for each state: https://web.archive.org/web/20130615162524/http://eric.clst.org/wupl/Stuff/gz_2010_us_040_00_500k.json
import outlineMap from "../../map_data/us-ca-outline.json";
import { FeatureCollection } from "geojson";
import { StoreModule } from "@/store/types";
import { GlobalActions, GlobalGetters } from "@/store/modules/global/types";
import { CustomerGetters } from "@/store/modules/customer/types";
import { namespace } from "vuex-class";
import CustomerService from "@/services/customer_service";
import polylabel from "polylabel";
import { EventBus } from "@/events/index";
import { SalesRegions, SalesRegionsStringType } from "@/models/map/region_type";
import TypeToViewMap, { CustomerStatus } from "@/models/customer";
import moment from "moment";
import { CustomerSchema } from "@/models/customer_schema";
import { Layers } from "@/models/map/layers";
import { CustomerType } from "@/models/customer";
import { RepActions } from "@/store/modules/rep/types";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { Roles } from "@/models/roles";
import { UserGetters } from "@/store/modules/user/types";
/**
 * Typescript does not support non-reactive instance variables.
 * The map object must be non-reactive; when it is reactive, the vue getters/setters will cause bugs.
 * The code below is a work-around to make the map non-reactive (interface)
 */
interface MapView {
  map: mapboxgl.Map;
}

@Component({
  components: {
    Mapbox,
    Loading,
  },
})
class MapView extends Vue {
  protected region = "" as SalesRegions["string"];
  protected selectedCustomer: null | number = null;
  private token = process.env.VUE_APP_MAP_TOKEN;
  protected coords = {} as mapboxgl.LngLat;
  protected markers: mapboxgl.Marker[] = [];
  protected markersStorage: mapboxgl.Marker[] = [];
  protected markerVisibility = false;
  protected customerService = new CustomerService();
  protected data: FeatureCollection = frankfordMap as FeatureCollection;
  protected dataOutline: FeatureCollection = outlineMap as FeatureCollection;
  protected showHistoryDiv = false;
  protected Layers = Layers;
  protected CustomerType = CustomerType;
  protected filter: "" | CustomerStatus = "";
  @(namespace(StoreModule.Global).Action(GlobalActions.AddLoading)) setLoading;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetLoading)) isLoading;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetActiveView))
  activeView;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddActiveView))
  addActiveView;
  @(namespace(StoreModule.Customer).Getter(CustomerGetters.GetAllCustomers))
  customers;
  @(namespace(StoreModule.Rep).Action(RepActions.SetSelectedRep))
  addActiveRep;
  @(namespace(StoreModule.User).Getter(UserGetters.GetRole)) role;
    @(namespace(StoreModule.User).Getter(UserGetters.GetUser)) user;
  /**
   * Global events that respond to event triggers in distant components
   *
   * @event handleRepSelection sets region of selected rep, triggered from Rep.vue
   * @event moveToCustomer moves to customer; triggered from Customer.vue
   * @event deleteCustomer remove marker of deleted customer from map. Triggered in ModalCustomer.vue
   * @event createNewMarker adds a new marker to the markers array on two conditions: a new customer/marker, or a modified location on an existing customer/marker.
   * Permanently remove marker with pre-updated location if existing parameter is passed (optional). Triggered in ModalCustomer.vue.
   *
   */

  mounted() {
    this.filter = CustomerStatus.Current;
    EventBus.$on(
      "handleRepSelection",
      (salesRegion: SalesRegions["string"]) => {
        this.region = salesRegion;
      }
    );
    EventBus.$on("moveToCustomer", () => {
      this.goCustomerView();
    });
    EventBus.$on("filterMarkers", (filter: CustomerStatus) => {
      this.filter = filter;
      if (this.markerVisibility) {
        this.displayMapMarkers();
      }
    });
    EventBus.$on(
      "addNewMarker",
      (newCustomer: CustomerSchema, existing?: boolean) => {
        if (existing) {
          for (const marker of this.markersStorage) {
            const id = marker.getElement().getAttribute("data-id");
            if (id!.toString() === newCustomer._id.toString()) {
              this.removeMarker(marker);
              this.deleteMarkerRecord(marker);
              const newMarker = this.createNewMarker(newCustomer);
              this.addMarkerToMap(newMarker);
            }
          }
        } else {
          const marker = this.createNewMarker(newCustomer);
          this.addMarkerToMap(marker);
        }
      }
    );
    EventBus.$on("deleteCustomer", (id: string) => {
      this.markersStorage.forEach((marker: mapboxgl.Marker) => {
        const markerId = marker.getElement().getAttribute("data-id");
        if (id.toString() === markerId) {
          this.removeMarker(marker);
          this.deleteMarkerRecord(marker);
        }
      });
    });
  }

  beforeDestroy() {
    EventBus.$off("handleRepSelection");
    EventBus.$off("moveToCustomer");
    EventBus.$off("filterMarkers");
    EventBus.$off("addNewMarker");
    EventBus.$off("deleteCustomer");
  }
  /**
   * @Watch (event listener)
   * Watches URL query + path parameters
   * Changes map view based on URL
   *
   * Enables map functionality on refresh, back buttons, navigating to hard-coded urls
   */
  @Watch("$route")
  protected onRouteChange(): void {
    this.setLoading(true);
    if (this.$route.path === "/" && !this.$route.query.region) {
      this.addActiveView(Layers.Customers);
      this.goFullView();
      this.addActiveRep({});
    } else if (this.$route.query.region) {
      this.addActiveView(Layers.Customers);
      this.goListView();
    } else if (this.$route.params.id) {
      this.goCustomerView();
    } else {
      if (this.activeView !== Layers.Customers && this.coords.lng) {
        this.flyToPoint(this.coords);
      } else {
        this.goFullView();
      }
    }
    setTimeout(() => this.setLoading(false), 200);
  }

  /**
   * @Watch (event listener)
   *
   * @param {Layers} oldValue
   * @param {Layers} newValue
   * Watches vuex store variable activeView, which dictates the current mapbox layer
   * Activates/deactivates layers on change
   *
   * The layers are states-layer, contract-layer, & beach-layer
   *
   * Since view is controlled by parsing the route (see @Watch $route), includes logic to build & route to correct URL
   *
   * oldValue === newValue handles the event on page refreshses
   *
   */
  @Watch("activeView")
  public toggleLayer(newValue: Layers, oldValue: Layers): void {
    if (this.role === Roles.SalesRep) {
      this.addActiveView(Layers.Customers);
    } else {
      this.hideMarkers();
      const selectedLayer: Layers = this.activeView;
      for (const layer of Object.values(Layers)) {
        if (layer === selectedLayer) {
          this.map.setLayoutProperty(layer, "visibility", "visible");
          this.map.setPaintProperty(layer, "fill-opacity", 0.5);
          this.map.setPaintProperty(layer, "fill-opacity-transition", {
            duration: 1000,
          });
        } else {
          this.map.setPaintProperty(layer, "fill-opacity", 0.0);
          this.map.setLayoutProperty(layer, "visibility", "none");
        }
      }
      if (selectedLayer !== Layers.Customers) {
        if (this.$route.params.id && oldValue === newValue) {
          this.goCustomerView();
        } else if (
          this.$route.path !== "/customers" ||
          this.$route.query.region
        ) {
          this.$router
            .push({ path: "/customers", query: {} })
            .catch((err) => {});
          this.goFullView();
        } else {
          this.goFullView();
        }
      } else {
        if (oldValue === newValue) {
          this.onPageRefresh();
        } else {
          this.$router.push({ path: "/", query: {} }).catch((err) => {});
        }
      }
    }
  }

  /**
   * @Watcher (event listener)
   *
   * Triggers global event on change of region property
   * Sends value to Main.vue
   *
   */
  @Watch("region", { immediate: true })
  protected onRegionChange(): void {
    this.$emit("sendRegion", this.region);
  }

  /**
   * Handles map view based on URL parameters
   * Differs from @Watch $route in that it can be called explicitly
   * Useful for page refreshes
   */
  public onPageRefresh(): void {
    if (this.$route.query.region) {
      this.goListView();
    } else if (this.$route.params.id && this.$route.name === "customers") {
      this.goCustomerView();
    } else if (this.role === Roles.SalesRep) {
      this.goListView();
    }
      else {
      this.goFullView();
    }
  }

  /**
   * Toggle cursor when hovering over/off map
   */
  public toggleCursor(show: boolean) {
    if (show) {
      this.map.getCanvas().style.cursor = "pointer";
    } else {
      this.map.getCanvas().style.cursor = "";
    }
  }

  /**
   * Look up the key in SalesRegionMap based on value
   *
   * The value is the {string} sales region & the key is the {int} code
   * Code corresponds to the number assigned to the region in the mapbox states-layer/JSON data
   *
   */
  public getSalesRegionCode(
    salesRegion: SalesRegions["string"]
  ): SalesRegions["code"] {
    let code;
    for (const [key, value] of SalesRegionMap.entries()) {
      if (value === salesRegion) code = key;
    }
    return code;
  }

  /**
   * Use the key (code) in SalesRegionMap to lookup information in the json map data
   */
  public mapJsonLookup(code: SalesRegions["code"]): GeoJSON.Geometry {
    const features = this.data.features.filter((feature) => {
      if (feature.properties) {
        return feature.properties.region === code;
      }
    });
    return features[0].geometry;
  }

  /**
   * @param {mapboxgl.Map} map map object
   * Called when the map loads.
   * Map is immediately saved as instance variable so it can be accessed through independent methods.
   * Map layers are added from the JSON data source and styled using the addLayer() method properties.
   */
  public loaded(map: mapboxgl.Map): void {
    this.initMapMarkers();
    this.map = map;
    map.resize();
    map.addSource("states", {
      type: "geojson",
      data: this.data,
    });
    // Add a layer showing the state polygons.
    map.addLayer({
      id: "states-layer",
      type: "fill",
      source: "states",
      layout: {
        visibility: "visible",
      },
      paint: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "region"],
          0,
          "black",
          100,
          "#FF3A1C",
          200,
          "#00584E",
          300,
          "#5FC5E9",
          400,
          "#003864",
          500,
          "#D30D2B",
          600,
          "#7AA752",
          700,
          "#FF5E00",
          800,
          "#A5652D",
          900,
          "#00ACC8",
          1000,
          "tan",
          1100,
          "#6E2581",
          1200,
          "#0480dd",
          1300,
          "#FEE543",
          1400,
          "salmon",
          1500,
          "#E8C900",
        ],
        "fill-opacity": 0, // start at 0 fill, then gradually transition into 0.5 (see below)
        "fill-opacity-transition": {
          duration: 2000,
        },
      },
    });
    // Toggling the colored states layer using fill-opacity transition creates a smoother transition vs. toggling visibility directly
    this.map.setPaintProperty("states-layer", "fill-opacity", 0.5);
    map.addSource("outline", {
      type: "geojson",
      data: this.dataOutline,
    });
    map.addLayer({
      id: "contract-layer",
      type: "fill",
      source: "outline",
      layout: {
        visibility: "none",
      },
      paint: {
        "fill-color": "#9ECA9A",
        "fill-opacity": 0, // start at 0 fill, then gradually transition into 0.5 (see below)
        "fill-opacity-transition": {
          duration: 500,
        },
      },
    });
    map.addLayer({
      id: "beach-layer",
      type: "fill",
      source: "outline",
      layout: {
        visibility: "none",
      },
      paint: {
        "fill-color": "#FDC976",
        "fill-opacity": 0, // start at 0 fill, then gradually transition into 0.5 (see below)
        "fill-opacity-transition": {
          duration: 500,
        },
      },
    });
    this.onPageRefresh();
    this.toggleLayer(this.activeView, this.activeView);
    this.setLoading(false);
  }

  /** Assigns map marker class name based on customer status
   * css will target this class name to assign appropriate icon as
   * the background image
   */
  public getMapMarkerLabel(
    status: CustomerStatus | "",
    created: string | undefined
  ): string {
    let label = "";
    switch (status) {
      case CustomerStatus.Current:
        if (created && moment().diff(moment(created), "years", true) <= 1) {
          label = "green";
        } else {
          label = "black";
        }
        break;
      case CustomerStatus.PriorityA:
        label = "purple";
        break;
      case CustomerStatus.PriorityB:
        label = "orange";
        break;
      case CustomerStatus.FrankfordTarget:
        label = "blue";
        break;
      case CustomerStatus.Lost:
        label = "red";
        break;
      default:
        label = "black";
    }
    return label;
  }

  /**
   * This method is called on component creation. Pulls in customer data to create map markers
   * and their associated popups. Stored in an instance variable so we only have to pull from the JSON data once per map load.
   *
   * This array will not be modified as the user navigates through the map. That will be done from the markers instance variable array.
   * This is meant to be the single source of truth for customer marker data.
   *
   */
  public initMapMarkers(): void {
    this.customers.forEach((customer) => {
      this.createNewMarker(customer);
    });
  }

  /**
   * @param {CustomerSchema} customer
   *
   * Convert coordinates to mapboxgl type
   * Create div element which will serve as our marker (docs point to this sequence as the best approach when using custom markers)
   * Add event listener to each marker (the default marker behavoir toggles popup on click)
   * Attach data to markers that is needed to filter marker visibility (data-region, data-type, data-id)
   * Create associated popup
   *
   * Attach coordinates and popup to marker and push to instance variable this.markersStorage array
   */
  public createNewMarker(customer: CustomerSchema): mapboxgl.Marker {
    const el = document.createElement("div");
    el.className = "marker";
    el.setAttribute("data-region", customer.sales_region);
    el.setAttribute("data-type", customer.customer_type);
    el.setAttribute("data-status", customer.status);
    el.setAttribute("data-id", customer._id.toString());
    const label = this.getMapMarkerLabel(
      customer.status,
      customer.customer_since!
    );
    el.className = label;
    // el.addEventListener("click", () => {
    //   if (this.$route.params.id != customer._id.toString()) {
    //     this.$router.push({ path: "/customers/" + customer._id });
    //   }
    // });
    const popup = new mapboxgl.Popup({
      closeOnClick: false,
      closeButton: false,
      offset: 20,
    }).setText(customer.customer_name);
    const coords = mapboxgl.LngLat.convert([
      customer.location.coordinates[0],
      customer.location.coordinates[1],
    ]);
    const marker = new mapboxgl.Marker(el).setLngLat(coords).setPopup(popup);
    const element = marker.getElement();
    element.addEventListener("mouseenter", () => popup.addTo(this.map));
    element.addEventListener("mouseleave", () => popup.remove());
    element.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        if (this.$route.params.id != customer._id.toString()) {
          this.$router
            .push({ path: "/customers/" + customer._id })
            .catch((err) => {});
        }
      },
      false
    );
    this.markersStorage.push(marker);
    return marker;
  }

  /**
   * @param {mapboxgl.Map} map map object
   * @param {mapboxgl.EventData} e map event object
   * Click event listener on map object.
   * Extracts region (from JSON data source properties object)
   *
   */
  public handleMapClickStates(map: mapboxgl.Map, e: mapboxgl.EventData): void {
    const regionCode = e.features[0].properties.region;
    const region: SalesRegions["string"] = SalesRegionMap.get(regionCode);
    this.region = region;
    if (this.$route.query.status) {
      this.$router
        .push({
          path: "/customers",
          query: { region: region, status: this.$route.query.status },
        })
        .catch((err) => {});
    } else {
      this.$router
        .push({ path: "/customers", query: { region: region } })
        .catch((err) => {});
    }
  }

  /**
   * @param {mapboxgl.Map} map map object
   * @param {mapboxgl.EventData} e map event object
   * Click event listener on map object.
   * Extracts region (from JSON data source properties object)
   *
   */
  public handleMapClickOther(map: mapboxgl.Map, e: mapboxgl.EventData): void {
    this.flyToPoint(e.lngLat);
    this.showMarkers();
  }

  /** Use polylabel plugin to find center of polygon
   * Includes failsafe if the algorithm cannot find center if the user clicked the map
   */
  public findCenter(
    coords: number[][][],
    clicked?: mapboxgl.LngLat
  ): mapboxgl.LngLat {
    let centerCoords;
    const center = polylabel(coords, 1.0);
    if (isNaN(center[0])) {
      centerCoords = clicked;
    } else {
      centerCoords = mapboxgl.LngLat.convert([center[0], center[1]]);
    }
    return centerCoords;
  }

  /**
   * @param {mapboxgl.lngLat} lngLat longitude & latitude
   * @param {int} zoom zoom level, defaults to 6
   * Fly to & zoom in on the passed in coordinates
   * Set this.coords instance variable for flying back to this spot in chidlFlyToSalesRegionString() method
   *
   */
  public flyToPoint(lngLat: mapboxgl.LngLat, zoom = 6): void {
    this.coords = lngLat;
    this.map.flyTo({ center: lngLat, zoom: zoom });
    const regionArray = this.customers.filter((customer) => {
      return (
        customer.sales_region === this.region &&
        (customer.customer_type === CustomerType.Customer ||
          customer.customer_type === CustomerType.Designer)
      );
    });
    EventBus.$emit("sendFooterData", regionArray);
  }

  /**
   * Goes from region view to default full map view
   * Hide markers and trigger visual map transition (when in customer/designer view)
   */
  public goFullView(): void {
            this.hideMarkers();
    if (this.activeView === Layers.Contract) {
      // This method provides a much smoother transition than toggling visibility of the layer
      this.map.setPaintProperty("contract-layer", "fill-opacity", 0.5);
      this.map.setLayoutProperty("contract-layer", "visibility", "visible");
    } else if (this.activeView === Layers.Beach) {
      // This method provides a much smoother transition than toggling visibility of the layer
      this.map.setPaintProperty("beach-layer", "fill-opacity", 0.5);
      this.map.setLayoutProperty("beach-layer", "visibility", "visible");
    } else {
      // This method provides a much smoother transition than toggling visibility of the layer
      this.map.setPaintProperty("states-layer", "fill-opacity", 0.5);
      this.map.setLayoutProperty("states-layer", "visibility", "visible");
    }
    this.map.flyTo({ zoom: 3 });
  }

  /**
   * Zoom in to customer
   * Pulls customer id from URL param & looks up longitude & latitude
   */
  public goCustomerView(): void {
    this.showMarkers();
    const customer = this.customers.filter((customer) => {
      return customer._id === this.$route.params.id.trim();
    });
    const coords = mapboxgl.LngLat.convert([
      customer[0].location.coordinates[0],
      customer[0].location.coordinates[1],
    ]);
    if (this.activeView !== TypeToViewMap.get(customer[0].customer_type)) {
      this.addActiveView(TypeToViewMap.get(customer[0].customer_type));
    }
    const region = customer[0].sales_region;
    this.region = region;
    this.flyToPoint(coords, 11);
  }

  /**
   * Flies to previously selected region from customer view
   * Hardcoded center coords are found here for specific regions
   */
  public goListView(): void {
    this.showMarkers();
    this.region = this.$route.query.region ? this.$route.query.region as SalesRegionsStringType : this.user.rep_info.sales_region as SalesRegionsStringType;
    const code = this.getSalesRegionCode(
      this.region as SalesRegionsStringType
    );
    const geometry = this.mapJsonLookup(code);
    let center: mapboxgl.LngLat;
    let zoom = 6;
    if (this.region === SalesRegionMap.get(1100)) {
      // Pacific Northwest - algorithm cannot handle Canadian territories, so use hardcoded center coords
      center = mapboxgl.LngLat.convert([-117.708208, 45.383176]);
    } else if (this.region === SalesRegionMap.get(600)) {
      // Midwest - algorithm cannot handle Canadian territories, so use hardcoded center coords
      center = mapboxgl.LngLat.convert([-84.92361, 41.768258]);
    } else if (this.region === SalesRegionMap.get(0)) {
      // Unassigned - these are all over the map, so hardcoding arbitrary center
      center = mapboxgl.LngLat.convert([-91.67777, 40.732936]);
      zoom = 4;
    } else if (this.region === SalesRegionMap.get(900)){
      // Southwest - region is too large to default to one particular state, so use hardcoded center coords
      center = mapboxgl.LngLat.convert([-109.722740, 40.988251]);
      zoom = 4.5;
    } else if (this.region === SalesRegionMap.get(200)){
      // South - region is too large to default to one particular state, so use hardcoded center coords
      center = mapboxgl.LngLat.convert([-81.001800, 35.024210]);
      zoom = 5.3;
    } else if (this.region === SalesRegionMap.get(400)){
      // Mid-Atlantic - region could be focused more, so use hardcoded center coords
      center = mapboxgl.LngLat.convert([-75.165222, 39.952583]);
      zoom = 6.3;
    } else {
      center = this.findCenter(geometry["coordinates"]);
    }
    this.flyToPoint(center, zoom);
  }

  /**
   * Toggles instance variable markerVisibility to true
   * The intention is this is to make sure markers only show when users select a region/are in
   * a zoomed in view. The map will lag if too many markers are shown.
   */
  public showMarkers(): void {
    this.map.setPaintProperty(this.activeView, "fill-opacity", 0);
    this.map.setLayoutProperty(this.activeView, "visibility", "none");
    this.markerVisibility = true;
  }

  /**
   * Toggles instance variable markerVisibility to false
   * Sets zoom
   * The intention is this is to make sure markers only show when users select a region/are in
   * a zoomed in view. The map will lag if too many markers are shown.
   * There is some buggy functionality based on where the zoom is set in the sequence of map actions.
   * Moving setting zoom from where it is now may cause skipping/unsmooth animations
   */
  public hideMarkers(): void {
    this.coords = {} as mapboxgl.LngLat;
    this.markerVisibility = false;
  }

  /**
   * Watches the markerVisibility instance variable. When it is set to false,
   * iterate through array of markers that are presently visible on map and remove them.
   * There is no method to remove all markers, they must be programatically stored in a variable and have the .remove() method
   * called on them one by one.
   *
   * NOTE this is NOT called on markersStorage array, but on markers. The latter only holds visible markers that we want to modify.
   */
  @Watch("markerVisibility", {})
  protected onChange(): void {
    if (!this.markerVisibility) {
      this.markers.forEach((marker) => {
        marker.remove();
      });
    }
  }

  /**
   * Map event capturing movement
   * If the map moves and markerVisibility is set to true,
   * adjust visible map markers by calling displayMapMarkers()
   */
  public handleMove(): void {
    if (this.markerVisibility) {
      this.displayMapMarkers();
    }
  }

  /**
   * This method updates markers on map movement
   *
   * Get the bounds of the current map view on the user's screen
   * Loops through markerStorage array and adds markers to map if their coordinates are within map bounds
   * and adds to markers array
   * (using the useful mapbox getBounds() and bounds.contains() methods)
   * Use .togglePop() method on markers added to map so popups default to open
   *
   * If markers are not within the bounds, remove them from the map and the markers array
   *
   * Logic of adding/removing markers from map is dependent on the active layer view/filtering (data-region, data-type)
   *
   */
  public displayMapMarkers(): void {
    const bounds = this.map.getBounds();
    this.markersStorage.forEach((marker: mapboxgl.Marker) => {
      const region = marker.getElement().getAttribute("data-region");
      const type = marker.getElement().getAttribute("data-type");
      const status = marker
        .getElement()
        .getAttribute("data-status") as CustomerStatus;
      if (
        (this.activeView === Layers.Customers &&
          bounds.contains(marker.getLngLat()) &&
          region === this.region &&
          type === CustomerType.Customer) ||
        type === CustomerType.Designer
      ) {
        if (this.filter && this.filter !== status) {
          this.removeMarker(marker);
        } else {
          this.addMarkerToMap(marker);
        }
      } else if (
        this.activeView === Layers.Contract &&
        bounds.contains(marker.getLngLat()) &&
        type === CustomerType.Contract
      ) {
        if (this.filter && this.filter !== status) {
          this.removeMarker(marker);
        } else {
          this.addMarkerToMap(marker);
        }
      } else if (
        this.activeView === Layers.Beach &&
        bounds.contains(marker.getLngLat()) &&
        type === CustomerType.BeachConcession
      ) {
        if (this.filter && this.filter !== status) {
          this.removeMarker(marker);
        } else {
          this.addMarkerToMap(marker);
        }
      } else {
        this.removeMarker(marker);
      }
    });
  }

  /**
   * Remove marker from view only
   * Marker remains unchanged in markersStorage
   */
  public removeMarker(marker: mapboxgl.Marker) {
    marker.remove();
    const index = this.markers.indexOf(marker);
    if (index > -1) {
      this.markers.splice(index, 1);
    }
  }

  /**
   * Remove marker from marker storage
   * Do not use this method to remove markers from the map view, use this.removeMarker()
   *
   */
  public async deleteMarkerRecord(marker: mapboxgl.Marker): Promise<void> {
    const index = this.markersStorage.indexOf(marker);
    if (index > -1) {
      this.markersStorage.splice(index, 1);
    }
  }

  /**
   * Add marker to map view only
   * Marker remains unchanged in markersStorage
   */
  public addMarkerToMap(marker: mapboxgl.Marker) {
    marker.addTo(this.map);
    this.markers.push(marker);
  }
}
export default MapView;
